import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Users,
  Clock,
  Settings,
  BarChart3,
  Calendar,
  FileText,
  Layers,
  TrendingUp,
  MessageSquare,
  Search,
  Send,
  ArrowRight,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_SUMMARY = [
  {
    icon: Building2,
    title: "Industry",
    desc: (
      <>
        <span>Financial Services</span>
        <span className="block text-[#64748B] mt-0.5">(Research & Analysis)</span>
      </>
    ),
  },
  {
    icon: Users,
    title: "Key Users",
    desc: "Research Head, Portfolio Managers, Analysis, Knowledge Teams",
  },
  {
    icon: Clock,
    title: "Business Need",
    desc: "Faster access to relevant financial information and higher-quality research insights",
  },
  {
    icon: Settings,
    title: "Solution",
    desc: "Simpel financial research assistant",
  },
  {
    icon: BarChart3,
    title: "Impact",
    desc: "40-60% reduction in research retrieval time",
  },
  {
    icon: Calendar,
    title: "Timeframe",
    desc: "8-14 months payback",
  },
];

const WHO_BENEFITS_DATA = [
  {
    role: "Head of Research / Analysis",
    improves:
      "Higher throughput and more consistent depth. Teams reach usable insight faster, coverage and quality both improve on the same capacity — cycle after cycle.",
  },
  {
    role: "Portfolio / Desk Lead",
    improves:
      "Faster support for live decisions. Relevant reports and prior internal work are available when the question is still open.",
  },
  {
    role: "Analyst / Associate",
    improves:
      "Less time assembling sources, more time interpreting them. First drafts and briefing packs start from ranked, cited material as a normal way of working.",
  },
  {
    role: "Knowledge / Content Lead",
    improves:
      "Internal research and reports are actively reused. Institutional knowledge stays visible in day-to-day work, not only in archives.",
  },
  {
    role: "IT / Information Security / Compliance",
    improves:
      "Controlled access to sensitive financial content. Role-based permissions, audit of use and integration with approved sources only.",
  },
];

const RESULTS_DATA = [
  {
    icon: Clock,
    subtitle: "Time spent on research retrieval (pilot teams)",
    title: "40% to 60% reduction",
  },
  {
    icon: FileText,
    subtitle: "Time to first usable insight pack / briefing",
    title: "45% to 65% faster",
  },
  {
    icon: Layers,
    subtitle: "Reuse of prior internal research and reports",
    title: "Clear increase in documented reuse",
  },
  {
    icon: BarChart3,
    subtitle: "Analyst hours on pure search vs analysis",
    title: "Shift toward analysis (measured on pilot)",
  },
  {
    icon: Calendar,
    subtitle: "Typical payback window",
    title: "8 - 14 months",
  },
  {
    icon: TrendingUp,
    subtitle: "First-year value (capacity + speed of insight)",
    title: "$200-$500k (scale-dependent)",
  },
];

const PROCESS_STEPS = [
  {
    id: "ask",
    icon: MessageSquare,
    bgGradient: "bg-gradient-to-br from-[#93C5FD] to-[#3B82F6]",
    glow: "shadow-[0_4px_18px_rgba(59,130,246,0.35)]",
    title: "Ask",
    desc: "Enter a research question in plain language.",
  },
  {
    id: "search",
    icon: Search,
    bgGradient: "bg-gradient-to-br from-[#E9D5FF] to-[#A855F7]",
    glow: "shadow-[0_4px_18px_rgba(168,85,247,0.35)]",
    title: "Search",
    desc: "AI searches approved sources and ranks relevant content.",
  },
  {
    id: "review",
    icon: FileText,
    bgGradient: "bg-gradient-to-br from-[#FEF08A] to-[#EAB308]",
    glow: "shadow-[0_4px_18px_rgba(234,179,8,0.35)]",
    title: "Review",
    desc: "Get cited answers with source links and key highlights.",
  },
  {
    id: "analyze",
    icon: BarChart3,
    bgGradient: "bg-gradient-to-br from-[#BBF7D0] to-[#22C55E]",
    glow: "shadow-[0_4px_18px_rgba(34,197,94,0.35)]",
    title: "Analyze",
    desc: "Build and refine your insights from the retrieved evidence.",
  },
  {
    id: "apply",
    icon: Send,
    bgGradient: "bg-gradient-to-br from-[#FBCFE8] to-[#EC4899]",
    glow: "shadow-[0_4px_18px_rgba(236,72,153,0.35)]",
    title: "Apply",
    desc: "Create reports, briefing packs or share insights with your team.",
  },
];

const FinancialResearchAssistant = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/cognitive");
    }
  };

  // Scroll tracking across the sticky track
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  /*
    Animation Timeline matching Automobile and Professional Services case studies:
    0.0 -> 0.22: Overlay enters from bottom (100% -> 0%), opacity (0 -> 1)
    0.22 -> 0.75: Overlay stays PINNED over Hero section (0% -> 0%, opacity 1)
    0.75 -> 0.98: Overlay slides UP out of view (0% -> -100%), opacity (1 -> 0)
    1.0+: Exits smoothly to next section
  */
  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#000000] flex flex-col text-white">
      {/* Top Navigation Bar matching Screenshot 1 */}
      <header className="sticky top-0 z-50 w-full bg-[#000000] border-b border-white/[0.06] shadow-sm">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Simpel Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button matching Screenshot 1 */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Industries / Financial Services
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Cognitive AI</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* Scroll Pinning Container (height allows smooth entry, pin duration, and exit) */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* 1. Underlying Hero Section Image (Financial Analyst Multi-Screen Setup) */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/financial-research-hero.jpg"
                alt="Financial Research Assistant - Market and Document Intelligence"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* 2. Frosted Blur Overlay Section matching Screenshot 3 */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint matching rgb(15, 27, 41) */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(15, 27, 41, 0.94)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              />

              {/* Foreground Content matching Screenshot 3 */}
              <div className="relative z-10 max-w-[1080px] flex flex-col items-center pt-4 sm:pt-0">
                {/* Category Tag */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-white/70 mb-5 sm:mb-6">
                  FINANCIAL SERVICES - RESEARCH &amp; ANALYSIS
                </p>

                {/* Main Heading with Highlighted Span */}
                <h1 className="text-[30px] sm:text-[42px] md:text-[50px] lg:text-[54px] font-bold leading-[1.2] sm:leading-[1.16] text-white tracking-tight">
                  Financial Research Assistant: From search to insight in a{" "}
                  <span className="text-[#2E90D7]">fraction of the time.</span>
                </h1>

                {/* Subtitle Paragraph */}
                <p className="mt-5 sm:mt-7 max-w-[820px] text-[13.5px] sm:text-[15px] md:text-[15.5px] leading-[24px] sm:leading-[26px] text-white/80">
                  How Simpel Financial Research Assistant searches financial information, reports, documents and internal knowledge to surface relevant insights faster — so analysts spend time judging, not hunting.
                </p>

                {/* Vertical Dashed Line with Downward Arrow */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-14 lg:mt-16 flex flex-col items-center cursor-pointer group select-none"
                  title="Scroll to explore"
                >
                  <svg
                    width="20"
                    height="126"
                    viewBox="0 0 20 126"
                    fill="none"
                    className="text-white/60 transition-transform duration-300 group-hover:translate-y-1.5"
                  >
                    <line
                      x1="10"
                      y1="0"
                      x2="10"
                      y2="114"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <path
                      d="M5 110L10 118L15 110"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            Case at a glance Section (matching Screenshot)
            Background: rgba(245, 250, 254, 1)
            6 Cards in a 3-Column x 2-Row Grid
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Heading row with gradient text and horizontal divider */}
            <div className="flex items-center justify-between mb-8 sm:mb-10">
              <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* Case Summary 6-Card Grid Container (3 cols x 2 rows) */}
            <div className="w-full rounded-[10px] sm:rounded-[12px] border border-[#EAF2FA] bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {CASE_SUMMARY.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className={`flex items-start gap-4 sm:gap-5 p-6 sm:p-7 min-h-[160px] sm:min-h-[175px] transition-colors duration-200 hover:bg-[#F8FAFC]/50 ${
                        idx % 3 !== 2 ? "lg:border-r border-[#EAF2FA]" : ""
                      } ${
                        idx < 3 ? "lg:border-b border-[#EAF2FA]" : ""
                      } ${
                        idx % 2 === 0 ? "md:max-lg:border-r border-[#EAF2FA]" : ""
                      } ${
                        idx < 4 ? "md:max-lg:border-b border-[#EAF2FA]" : ""
                      } ${
                        idx < 5 ? "max-md:border-b border-[#EAF2FA]" : ""
                      }`}
                    >
                      {/* Icon Circle Badge */}
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                        <Icon className="h-6 w-6 stroke-[2]" />
                      </div>

                      {/* Content */}
                      <div className="flex flex-col gap-1.5 pt-0.5">
                        <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] leading-tight">
                          {item.title}
                        </h3>
                        <div className="text-[13px] sm:text-[13.5px] leading-[22px] text-[#64748B]">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            1. The Context Section (matching Screenshot 1)
            Background: /images/insights/financial-context-bg.jpg
            Left: The Context heading (gradient) + divider line + 2 narrative paragraphs
            Right: Financial analyst with dual monitors (visible in image)
           ========================================================================= */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#020B18] py-14 sm:py-18 lg:py-20 min-h-[420px] lg:min-h-[460px] flex items-center"
        >
          {/* Base Background Image (Analyst looking at dual monitors) */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/financial-context-bg.jpg"
              alt="Financial Analyst Research Context"
              className="h-full w-full object-cover object-[center_right] sm:object-center"
            />
          </div>

          {/* Smooth dark vignette / gradient on the left so the text is pristine */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#020B18] via-[#020B18]/90 sm:via-[#020B18]/80 to-transparent w-full md:w-[68%] lg:w-[58%] pointer-events-none" />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="max-w-[480px] lg:max-w-[500px]">
              {/* Heading row with horizontal divider line */}
              <div className="flex items-center gap-6 mb-6 sm:mb-7">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Context
                </h2>
                <div className="flex-1 h-[1px] bg-slate-700/60" />
              </div>

              {/* 2 Paragraphs */}
              <p className="text-[13px] sm:text-[13.5px] leading-[22px] sm:leading-[24px] text-white/80">
                Financial research has always been a race between the volume of information and the time available to use it. Filings, market reports, internal memos, prior analysis and client history keep growing. The quality of a recommendation still depends on finding the right material before the decision window closes.
              </p>

              <p className="mt-4 sm:mt-5 text-[13px] sm:text-[13.5px] leading-[22px] sm:leading-[24px] text-white/80">
                When material is spread across terminals, shared drives and email, analysts burn hours assembling context. A single assistant that searches financial information, reports, documents and internal knowledge and returns cited insights quickly helps teams deliver deeper work in less calendar time.
              </p>
            </div>
          </div>
        </section>

        {/* =========================================================================
            2. What the solution does Section (matching Screenshot 3)
            Background: rgba(245, 250, 254, 1)
            Left: Laptop display photo showing Simpel Financial Research Assistant interface
            Right: What the solution does heading (gradient) + 2 detailed paragraphs
           ========================================================================= */}
        <section
          id="what-the-solution-does"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Laptop Display Image */}
              <div className="lg:col-span-6 w-full flex justify-center">
                <div className="w-full overflow-hidden rounded-[14px] sm:rounded-[18px] shadow-[0_12px_40px_rgba(15,23,42,0.08)] bg-white border border-[#E2EDF8]">
                  <img
                    src="/images/insights/financial-solution-laptop.jpg"
                    alt="Financial Research Assistant interface on Laptop"
                    className="w-full h-auto object-cover block select-none"
                  />
                </div>
              </div>

              {/* Right Column: Narrative Content */}
              <div className="lg:col-span-6 flex flex-col justify-center">
                {/* Heading row with horizontal divider line */}
                <div className="flex items-center gap-6 mb-5 sm:mb-6">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    What the solution does
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                {/* Paragraph 1 */}
                <p className="text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Analysts and associates ask questions in plain language. The Assistant searches approved financial sources — reports, filings, internal research, models and knowledge bases — and surfaces ranked, cited answers. External and internal material can be combined under policy. Role-based access and audit trails protect sensitive content. The Assistant does not replace the analyst&apos;s judgement; it removes the lag between the question and the evidence needed to answer it — every time the question is asked.
                </p>

                {/* Paragraph 2 */}
                <p className="mt-5 sm:mt-6 text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Simpel AI (simpel.ai) designs and delivers the end-to-end solution: understand research workflows and sources, design the assistant for real desk practice, build with the right AI and controls, integrate with existing systems and data rooms, and scale from a pilot team to broader coverage — with measurable impact on time-to-insight and research throughput that holds across cycles.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            3. The challenge Section (matching Screenshot 5)
            Background: #FFFFFF (White)
            Left: The challenge heading (gradient) + horizontal divider line + narrative paragraph
            Right: 2 Cards (01 Information overload, 02 Time pressure)
           ========================================================================= */}
        <section
          id="the-challenge"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left Column: Heading & Intro Paragraph */}
              <div className="lg:col-span-5 flex flex-col justify-center">
                {/* Heading row with horizontal divider line */}
                <div className="flex items-center gap-6 mb-5 sm:mb-6">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The challenge
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                {/* Intro Narrative */}
                <p className="text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  The volume of information keeps growing. Analysts often spend significant time understanding requirements, searching across multiple sources and assembling context before they can focus on interpretation and insights.
                </p>
              </div>

              {/* Right Column: 2 Cards (01 & 02) */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Card 01 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      01
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Information overload
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Filings, reports and internal notes are spread across multiple systems, making it time-consuming to find the right material.
                    </p>
                  </div>
                </div>

                {/* Card 02 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#2B7FFF] tracking-tight leading-none">
                      02
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Time pressure
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Insights are needed quickly to support live decisions. Teams must produce high-quality analysis with limited time and resources.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            4. Who benefits Section (matching Screenshot 1)
            Background: /images/insights/financial-who-benefits-bg.jpg
            Left: Who benefits heading (gradient) + horizontal line (#CEDDF1) + subtitle
            Table: 5 Roles with glassmorphic styling (rgba(11,22,44,0.5), rgba(30,41,59,1), blur 30px)
           ========================================================================= */}
        <section
          id="who-benefits"
          className="relative w-full overflow-hidden bg-[#020B18] py-16 sm:py-20 lg:py-24 text-white"
        >
          {/* Base Background Image */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/financial-who-benefits-bg.jpg"
              alt="Who Benefits Background"
              className="h-full w-full object-cover object-center"
            />
          </div>

          {/* Dark Glass Overlay */}
          <div className="absolute inset-0 bg-[#020B18]/85 backdrop-blur-[2px] pointer-events-none" />

          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with gradient text and exact horizontal divider matching Screenshot 2 (#CEDDF1) */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Who benefits
              </h2>
              <div className="flex-1 h-[1px] bg-[#CEDDF1]" />
            </div>

            {/* Subtitle */}
            <p className="text-[13.5px] sm:text-[14.5px] leading-[22px] text-white/80 mb-8 sm:mb-10 max-w-[840px]">
              Five roles gain when research keeps pace with the market and the client — continuously, not only on a single project.
            </p>

            {/* Roles Table Container matching Screenshot 1 Figma specs */}
            <div
              style={{
                backgroundColor: "rgba(9, 21, 40, 0.28)",
                borderColor: "rgba(30, 41, 59, 1)",
                borderWidth: "1px",
                borderStyle: "solid",
                boxShadow:
                  "0 8px 10px -6px rgba(0, 0, 0, 0.1), 0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              }}
              className="w-full rounded-[14px] overflow-hidden"
            >
              {/* Table Header */}
              <div
                style={{
                  backgroundColor: "rgba(14, 29, 53, 0.65)",
                  borderBottomColor: "rgba(30, 41, 59, 1)",
                }}
                className="grid grid-cols-12 px-6 py-4 border-b text-[11px] sm:text-[12px] font-bold tracking-[1.5px] uppercase text-white/90"
              >
                <div className="col-span-12 sm:col-span-4 lg:col-span-4">ROLE</div>
                <div className="hidden sm:block sm:col-span-8 lg:col-span-8">WHAT IMPROVES FOR THEM</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y" style={{ borderColor: "rgba(30, 41, 59, 1)" }}>
                {WHO_BENEFITS_DATA.map((item) => (
                  <div
                    key={item.role}
                    style={{ borderColor: "rgba(30, 41, 59, 1)" }}
                    className="grid grid-cols-12 px-6 py-5 sm:py-6 items-start gap-2 sm:gap-6 hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-4 lg:col-span-4 text-[14.5px] sm:text-[15.5px] font-bold text-white leading-snug">
                      {item.role}
                    </div>
                    <div className="col-span-12 sm:col-span-8 lg:col-span-8 text-[13px] sm:text-[13.5px] leading-[22px] text-white/80">
                      <span className="sm:hidden text-[11px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">
                        What improves:
                      </span>
                      {item.improves}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            5. Results firms typically see Section (matching Screenshot 3)
            Background: rgba(245, 250, 254, 1)
            6 Metric Cards (3 cols x 2 rows)
           ========================================================================= */}
        <section
          id="results-firms-typically-see"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Results firms typically see
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {RESULTS_DATA.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {card.subtitle}
                      </span>
                      <h3 className="text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                        {card.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            6. What other lenders can take from this Section (matching Screenshot 4)
            Background: rgba(255, 255, 255, 1)
            Process Flow: Ask -> Search -> Review -> Analyze -> Apply
           ========================================================================= */}
        <section
          id="what-lenders-can-take"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                What other lenders can take from this
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Subtitle (1 line on desktop matching user requirement) */}
            <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[22px] text-[#64748B] mb-12 sm:mb-16 whitespace-normal lg:whitespace-nowrap">
              A question comes in. The assistant finds relevant sources, surfaces evidence, and helps analysts turn research into actionable insights faster.
            </p>

            {/* 5-Step Process Flow with Connectors */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-2">
              {PROCESS_STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[210px] w-full group">
                      {/* Colored Circle Badge */}
                      <div
                        className={`flex h-16 w-16 sm:h-18 sm:w-18 items-center justify-center rounded-full ${step.bgGradient} ${step.glow} text-white transition-transform duration-300 group-hover:scale-110 mb-4 sm:mb-5`}
                      >
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 stroke-[2.2]" />
                      </div>

                      {/* Step Title */}
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] mb-2">
                        {step.title}
                      </h3>

                      {/* Step Description */}
                      <p className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps (hidden on last step) */}
                    {idx < PROCESS_STEPS.length - 1 && (
                      <div className="hidden lg:flex items-center justify-center shrink-0 px-2 text-[#00D2EE]">
                        <ArrowRight className="h-6 w-6 stroke-[2.5]" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            7. CTA Banner Section (exact Figma specs: 1440px x 340px)
            Background: /images/insights/financial-context-bg.jpg
            Figma Gradient: rgba(5, 11, 20, 1) -> rgba(5, 11, 20, 0.9) -> rgba(5, 11, 20, 0)
            Progressive Blur: 45.73% (blur 14) to 64.69% (blur 0)
            Left: Heading "Turn research into sharper decisions." (1 line) + 2-line subtitle + CTA button
           ========================================================================= */}
        <section
          id="cta-banner"
          className="relative w-full overflow-hidden bg-[#050B14] flex items-center min-h-[340px] lg:h-[340px] text-white"
        >
          {/* Base Background Image: Analyst with dual monitors on the right */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/financial-context-bg.jpg"
              alt="Turn research into sharper decisions"
              className="h-full w-full object-cover object-[center_right] sm:object-[82%_center]"
            />
          </div>

          {/* Figma Linear Gradient Overlay: rgba(5, 11, 20, 1) -> rgba(5, 11, 20, 0.9) -> rgba(5, 11, 20, 0) */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(5, 11, 20, 1) 0%, rgba(5, 11, 20, 0.98) 35%, rgba(5, 11, 20, 0.9) 45.73%, rgba(5, 11, 20, 0) 64.69%)",
            }}
          />

          {/* Figma Progressive Blur Layer (Start: 45.73% blur 14 -> End: 64.69% blur 0) */}
          <div
            className="absolute inset-y-0 left-0 w-[65%] pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(5, 11, 20, 0.8) 0%, rgba(5, 11, 20, 0.6) 45.73%, rgba(5, 11, 20, 0) 64.69%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              maskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
              WebkitMaskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
            }}
          />

          {/* Content Container (Max width 1440px matching Figma layout) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0 flex flex-col justify-center">
            <div className="max-w-[700px] xl:max-w-[760px] w-full">
              {/* Heading: exact 1 line matching Screenshot 2 */}
              <h2 className="text-[26px] sm:text-[32px] md:text-[36px] lg:text-[38px] xl:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent leading-[1.2] whitespace-normal sm:whitespace-nowrap">
                Turn research into sharper decisions.
              </h2>

              {/* Subtitle: exact 2 lines matching Screenshot 2 */}
              <div className="mt-3.5 sm:mt-4 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[22px] sm:leading-[24px] text-white/85">
                <p className="whitespace-normal sm:whitespace-nowrap">
                  See how Simpel Financial Research Assistant can help your team find relevant insights
                </p>
                <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                  faster and deliver higher-quality analysis.
                </p>
              </div>

              {/* Consultation Button */}
              <div className="mt-6 sm:mt-7">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 sm:px-7 sm:py-3 text-[13.5px] sm:text-[14px] font-bold text-[#0F172A] transition-all duration-200 hover:bg-slate-100 hover:shadow-[0_8px_24px_rgba(255,255,255,0.25)] hover:scale-105 active:scale-95"
                >
                  <span>Request a Consultation</span>
                  <ArrowRight className="h-4 w-4 stroke-[2.5]" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default FinancialResearchAssistant;

